import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Layout } from './components/Layout.js';
import { useAuth } from './lib/auth.js';
import { Button, Loading } from './components/ui.js';
import { AuthPage } from './pages/AuthPage.js';
import { FeedPage } from './pages/FeedPage.js';
import { FriendsPage } from './pages/FriendsPage.js';
import { GiftsPage } from './pages/GiftsPage.js';
import { HomePage } from './pages/HomePage.js';
import { ListPage } from './pages/ListPage.js';
import { ProfilePage } from './pages/ProfilePage.js';
import { SharePage } from './pages/SharePage.js';
import { UserPage } from './pages/UserPage.js';
import { AddFromPagePage } from './pages/AddFromPagePage.js';
import { BookmarkletPage } from './pages/BookmarkletPage.js';
import { ShareTargetPage } from './pages/ShareTargetPage.js';
import { ResetPasswordPage } from './pages/ResetPasswordPage.js';
import { PrivacyPage } from './pages/PrivacyPage.js';

/**
 * Ждём ли мы уже дольше, чем человек готов смотреть на молчащую крутилку.
 *
 * Семь секунд — это заметно больше обычного открытия (доли секунды) и заметно
 * меньше срока, после которого запрос считается оборванным. То есть строчка
 * появляется как раз в промежутке, когда ещё есть надежда, но уже есть тревога.
 */
function ДолгоЛиЖдём(ждём: boolean): boolean {
  const [долго, поставить] = useState(false);

  useEffect(() => {
    if (!ждём) {
      поставить(false);
      return undefined;
    }
    const таймер = setTimeout(() => поставить(true), 7_000);
    return () => clearTimeout(таймер);
  }, [ждём]);

  return долго;
}

/**
 * Маршруты приложения.
 *
 * Единственный публичный адрес — страница списка по секретной ссылке: ею делятся
 * в мессенджере, и требовать регистрации, чтобы просто посмотреть чужой вишлист,
 * было бы враждебно. Всё остальное — только для вошедших.
 */

export function App(): JSX.Element {
  const { user, isLoading, сетьНеОтветила, спроситьЗаново } = useAuth();
  const долго = ДолгоЛиЖдём(isLoading);

  if (isLoading) {
    return (
      <div className="app-shell">
        <main className="app-main">
          <Loading label="Открываем Загадарь…" />
          {/*
            Через несколько секунд говорим, что связь медленная.
            Молчащая крутилка не отличима от зависшей — человек с телефоном
            в руке решает, что приложение сломалось, и закрывает его. Одна
            строчка снимает этот вопрос, ничего при этом не обещая.
          */}
          {долго && (
            <p className="muted small" style={{ textAlign: 'center' }}>
              Связь медленная — ещё пробуем…
            </p>
          )}
        </main>
      </div>
    );
  }

  /*
   * Связи нет — говорим об этом, а не показываем форму входа.
   *
   * Это место было главной поломкой в мобильной сети. Запрос «кто я» уходил
   * без срока ожидания, замирал вместе с соединением и не кончался ни успехом,
   * ни ошибкой: человек смотрел на «Открываем Загадарь…» столько, сколько
   * хватало терпения. Срок теперь есть, но одного его мало — без этого экрана
   * человека выкидывало бы на вход, хотя сессия жива и он никуда не выходил.
   */
  if (сетьНеОтветила) {
    return (
      <div className="app-shell">
        <main className="app-main">
          <div className="empty" role="alert">
            <h2>Не получилось связаться с сервером</h2>
            <p className="muted small">
              Похоже, пропала связь. Проверьте интернет — и попробуйте ещё раз. Вы никуда
              не вышли: как только связь появится, всё откроется как было.
            </p>
            <Button onClick={спроситьЗаново}>Попробовать снова</Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <Routes>
      {/* Просмотр по секретной ссылке доступен без входа. */}
      <Route path="/s/:token" element={<SharePage />} />

      {/*
        Приёмник ссылок из других приложений работает и до входа: он лишь
        сохраняет ссылку и перенаправляет на главную. Если человек не вошёл,
        его встретит форма входа, а ссылка дождётся его в sessionStorage.
      */}
      <Route path="/share-target" element={<ShareTargetPage />} />
      <Route path="/add" element={<AddFromPagePage />} />
      <Route path="/bookmarklet" element={<BookmarkletPage />} />

      {/*
        Установка нового пароля доступна без входа — человек как раз не может
        войти, в этом и смысл.
      */}
      <Route path="/reset" element={<ResetPasswordPage />} />

      {/*
        Правила обработки данных читают до регистрации, а не после, —
        поэтому страница открыта всем.
      */}
      <Route path="/privacy" element={<PrivacyPage />} />

      {!user ? (
        <>
          <Route path="/*" element={<AuthPage />} />
        </>
      ) : (
        <Route element={<Layout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/lists/:listId" element={<ListPage />} />
          <Route path="/feed" element={<FeedPage />} />
          <Route path="/friends" element={<FriendsPage />} />
          <Route path="/gifts" element={<GiftsPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/u/:username" element={<UserPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      )}
    </Routes>
  );
}
